/**
 * DASHBOARD-RPS-GOLLAC-QUESTIONS.WIDGET
 * Gollac exposure grouped by Axis → Topic → Questions
 */

import Card from "@/components/card"
import { useEffect, useState } from "react"
import { DashboardFilters } from "@/redux/filter.types"
import { store } from "@/index"
import { INSUFFICIENT_PARTICIPATION, STATUS_LOADED, STATUS_LOADING } from "@/redux/_status.types"
import NoResultsWidget from "./no-results.widget"
import { sum } from "lodash"
import getRepartitionColor from "@/utils/get-repartition-color.utils"
import { fetchQuestions } from "@/redux/_archive.actions"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheckCircle, faExclamationCircle, faInfoCircle } from "@fortawesome/free-solid-svg-icons"
import { useAppSelector } from "@/core/root-reducer"
import { t } from "i18next"
import ListCell from "@/components/list-cell"
import Tooltip from "@/components/tooltip"
import { getRepartitionCeils } from "@/utils/get-repartition-ceils.utils"
import { motion } from "framer-motion"
import { staggerDelay } from "@/utils/stagger-delay.utils"
import DashboardRpsGollacQuestionsSkeletonWidget from "./skeletons/dashboard-rps-gollac-questions-skeleton.widget"

/* -------------------- TYPES -------------------- */

type GollacZone = "exposed" | "safe"

interface GollacQuestion {
  id: number
  label: string
  repartition: number[]
  total: number
  negativeRate: number
  note: number
}

type TopicTree = Record<string, GollacQuestion[]>
type AxisTree = Record<string, TopicTree>

interface GollacTree {
  exposed: AxisTree
  safe: AxisTree
}

interface Props {
  dashboardFilters: DashboardFilters
  isReport?: boolean
  isPreview?: boolean
  limit?: number
  offset?: number
  zone?: "exposed" | "safe"
}

/* -------------------- CONSTANTS -------------------- */

const ALLOWED_SPECIALS = new Set([
  "psychological-job-demands",
  "decision-latitude",
  "skill-use",
  "team-support",
  "manager-support",
  "reconnaissance"
])


/* -------------------- COMPONENT -------------------- */

export default function DashboardRpsGollacQuestionsWidget(props: Props) {
  const session = useAppSelector((state) => state._session)
  const ceilThresholdGollac = getRepartitionCeils(session, true, false)[0] * 10
  const topicState = useAppSelector((state) => state.topic)
  const axisState = useAppSelector((state) => state.axis)

  const [status, setStatus] = useState(STATUS_LOADING)
  const [tree, setTree] = useState<GollacTree>({
    exposed: {},
    safe: {}
  })

  useEffect(() => {
    async function load() {
      setStatus(STATUS_LOADING)

      const data: any = await store.dispatch(
        fetchQuestions(props.dashboardFilters, null, undefined, undefined, props.isReport)
      )

      if (data?.error) {
        setStatus(STATUS_LOADED)
        return
      }

      const newTree: GollacTree = {
        exposed: {},
        safe: {}
      }

      topicState.list.forEach((tp) => {
        tp.Questions.forEach((q: any) => {
          if (!ALLOWED_SPECIALS.has(q.special)) return

          const current = data.find((x: any) => x.aid === q.aid)
          if (!current) return

          const repartition = current.repartition ?? []
          const total = sum(repartition)
          if (!total) return

          const negatives = repartition[0] + (repartition[1] ?? 0)
          const negativeRate = (negatives / total) * 100
          const zone: GollacZone = current.note < ceilThresholdGollac ? "exposed" : "safe"

          if (!tp.AxisId) return

          if (!newTree[zone][tp.AxisId]) {
            newTree[zone][tp.AxisId] = {}
          }

          if (!newTree[zone][tp.AxisId][tp.id]) {
            newTree[zone][tp.AxisId][tp.id] = []
          }

          newTree[zone][tp.AxisId][tp.id].push({
            id: q.id,
            label: q.label,
            repartition,
            total,
            negativeRate,
            note: current.note
          })
        })
      })

      // ---- FALLBACK IF NO EXPOSED ----
      const hasExposed = Object.keys(newTree.exposed).length > 0

      if (!hasExposed) {
        let worst: any = null
        let worstAxisId: string | null = null
        let worstTopicId: string | null = null

        Object.entries(newTree.safe).forEach(([axisId, topics]: any) => {
          Object.entries(topics).forEach(([topicId, questions]: any) => {
            questions.forEach((q: any) => {
              if (!worst || q.note < worst.note) {
                worst = q
                worstAxisId = axisId
                worstTopicId = topicId
              }
            })
          })
        })

        if (worst && worstAxisId && worstTopicId) {
          newTree.safe[worstAxisId][worstTopicId] = newTree.safe[worstAxisId][worstTopicId].filter(
            (q: any) => q.id !== worst.id
          )

          if (newTree.safe[worstAxisId][worstTopicId].length === 0) {
            delete newTree.safe[worstAxisId][worstTopicId]
          }

          if (!newTree.exposed[worstAxisId]) newTree.exposed[worstAxisId] = {}
          if (!newTree.exposed[worstAxisId][worstTopicId]) newTree.exposed[worstAxisId][worstTopicId] = []

          newTree.exposed[worstAxisId][worstTopicId].push(worst)
        }
      }

      setTree(newTree)
      setStatus(STATUS_LOADED)
    }

    load()
  }, [topicState.list, props.dashboardFilters])

  if (status === INSUFFICIENT_PARTICIPATION) {
    return <NoResultsWidget />
  }

  return (
    <Card helpSection="onglets"
      title={
        <div className="card-title flex items-center">
          <span> {t("dashboard_compare_simple")}</span>
          <Tooltip text={t("rps_question_help")}>
            <FontAwesomeIcon icon={faInfoCircle} className="grey-t ml-2 cursor-help" />
          </Tooltip>
        </div>
      }
      className="flex1"
      status={status}
      exportName={t("rps_user_title")}
      skeleton={<DashboardRpsGollacQuestionsSkeletonWidget />}>
      {(!props.zone || props.zone === "exposed") && (
        <Zone
          zone="exposed"
          tree={tree.exposed}
          axisList={axisState.list}
          topicList={topicState.list}
          session={session}
          icon={faExclamationCircle}
          colorClass="red-t"
          title="subjects_exposed"
          isReport={props.isReport}
          isPreview={props.isPreview}
          limit={props.limit}
          offset={props.offset}
        />
      )}

      {(!props.zone || props.zone === "safe") && (
        <Zone
          zone="safe"
          tree={tree.safe}
          axisList={axisState.list}
          topicList={topicState.list}
          session={session}
          icon={faCheckCircle}
          colorClass="green-t"
          title="subjects_secured"
          isReport={props.isReport}
          isPreview={props.isPreview}
          limit={props.limit}
          offset={props.offset}
        />
      )}
    </Card>
  )
}

/* -------------------- ZONE -------------------- */

function Zone({ tree, axisList, topicList, session, icon, colorClass, title, isPreview, limit, offset }: any) {
  const rows: { question: any; topic: any; axis: any }[] = []

  Object.entries(tree).forEach(([axisId, topics]: any) => {
    const axis = axisList.find((a: any) => a.id === axisId)
    Object.entries(topics).forEach(([topicId, questions]: any) => {
      const topic = topicList.find((t: any) => t.id === topicId)
      questions.forEach((q: any) => rows.push({ question: q, topic, axis }))
    })
  })

  rows.sort((a, b) => a.question.note - b.question.note)

  // Appliquer la pagination si en preview/report
  let visibleRows = rows
  if (isPreview && limit !== undefined) {
    const offsetValue = offset ?? 0
    visibleRows = rows.slice(offsetValue, offsetValue + limit)
  }

  return (
    <div className="mb-40">
      <h2 className={colorClass}>
        <FontAwesomeIcon icon={icon} /> {t(title)}
      </h2>

      {visibleRows.length === 0 && (
        <div style={{ margin: "20px 0 40px 0" }}>
          <i>{t(title === "subjects_exposed" ? "no_exposed_subjects" : "no_secured_subjects")}</i>
        </div>
      )}

      {visibleRows.map(({ question: q, topic, axis }, index) => (
        <div
          key={q.id}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 24,
            padding: "12px 0",
            borderTop: index === 0 ? "none" : "1px solid #e5e7eb"
          }}>
          <div className="flex1">
            <div style={{ color: axis?.color }}>
              <span className="font-bold">{topic?.label}</span>
              <span> - {axis?.label}</span>
            </div>
            <div>{q.label}</div>
          </div>
          <RepartitionBar question={q} session={session} />
        </div>
      ))}
    </div>
  )
}

/* -------------------- REPARTITION -------------------- */

function RepartitionBar({ question, session }: any) {
  return (
    <ListCell width={280}>
      <div className="mx-auto">
        <div
          style={{
            height: 18,
            borderRadius: 12,
            overflow: "hidden",
            display: "flex",
            background: "#e5e7eb"
          }}>
          {question.repartition.map((count: number, i: number) => (
            <motion.div
              key={i}
              style={{
                backgroundColor: getRepartitionColor(
                  i,
                  question.repartition.length - 1,
                  true,
                  session.dashboardDisplaySettings,
                  false
                )
              }}
              initial={{ width: 0 }}
              animate={{ width: (count / question.total) * 100 + "%" }}
              transition={{ duration: 0.5, delay: staggerDelay(i, 0.06), ease: [0.34, 1.56, 0.64, 1] }}
            />
          ))}
        </div>

        <div
          className="grey-t"
          style={{
            fontSize: 13,
            marginTop: 6,
            textAlign: "center"
          }}>
          {t("rps_responses_negatives", {
            ratio: question.negativeRate.toFixed(1)
          })}
        </div>
      </div>
    </ListCell>
  )
}
